<template>
	<BaseCard
		class="flex items-center gap-4 p-4"
		v-hover="{ active: 'scale-95', impact: 'soft' }"
		@click="open"
	>
		<div class="bg-fill-secondary h-11 w-11 shrink-0 rounded-full p-2.5">
			<img :src="src" alt="" class="h-6 w-6" />
		</div>
		<div class="min-w-0 flex-1">
			<div class="text-label-secondary text-xs font-medium uppercase">
				{{ title }}
			</div>
			<div
				class="text-label-primary mt-1 min-w-0 truncate text-sm font-medium capitalize empty:hidden"
			>
				{{ desc }}
			</div>
		</div>
		<CoinPrice class="shrink-0" v-if="price" :price="price" />
		<EmptyIcon class="text-fill-secondary h-5 w-5" v-else />
		<CaseOpenModal
			v-if="
				(type === 'case_open' ||
					type === 'reward_case_open' ||
					type === 'referral_case_open' ||
					type === 'welcome_case_open') &&
				box
			"
			:box="box"
			:item="item"
			:fair="fairArray"
			:date="date"
			v-model="view"
			@openFairness="openCaseFairness"
		/>
		<CaseFairModal
			v-if="
				(type === 'case_open' ||
					type === 'reward_case_open' ||
					type === 'referral_case_open' ||
					type === 'welcome_case_open') &&
				fairArray
			"
			:fair="fairArray"
			v-model="caseFairness"
		/>
	</BaseCard>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseCard from './BaseCard.vue'
import { caseIcon, icon } from '@/utils/urls'
import tonIcon from '@/assets/icons/ton.svg?url'
import usdtIcon from '@/assets/icons/usdt.svg?url'
import starsIcon from '@/assets/icons/stars.svg?url'
import swordIcon from '@/assets/icons/sword.svg?url'
import boxIcon from '@/assets/icons/box.svg?url'
import CoinPrice from './CoinPrice.vue'
import EmptyIcon from '@/assets/icons/empty.svg'
import { useI18n } from 'vue-i18n'
import CaseOpenModal from '@/components/modals/transactions/CaseOpenModal.vue'
import CaseFairModal from '@/components/modals/CaseFairModal.vue'

const { t } = useI18n({
	messages: {
		en: {
			deposit: 'Deposit',
			withdraw: 'Withdraw',
			caseOpened: 'Case Opened',
			itemSold: 'Item Sold',
			battleCreated: 'Battle Created',
			battleJoined: 'Battle Joined',
			itemClaimed: 'Item Claimed',
			rewardCaseOpened: 'Reward Case Opened',
			unknownItem: 'Unknown Item',
			unknownCase: 'Unknown Case',
			tonRefill: 'TON Refill',
			tonTetherRefill: 'TON Tether Refill',
			marketBuy: 'Market Purchase',
			battleReward: 'Battle Reward',
			cryptoBotRefill: 'Crypto Bot Refill',
			starsRefill: 'Stars Refill',
			battleRefund: 'Battle Refund',
			adminRefill: 'Admin Refill ❤️',
			rainReward: 'Rain Reward',
			rainTip: 'Rain Tip',
			tip: 'Tip'
		},
		ru: {
			deposit: 'Депозит',
			withdraw: 'Вывод',
			caseOpened: 'Открытие кейса',
			itemSold: 'Продажа гифта',
			battleCreated: 'Создание батла',
			battleJoined: 'Вступление в батл',
			itemClaimed: 'Вывод гифта',
			rewardCaseOpened: 'Открытие награды',
			unknownItem: 'Неизвестный гифт',
			unknownCase: 'Неизвестный кейс',
			tonRefill: 'Пополнение TON',
			tonTetherRefill: 'Пополнение TON Tether',
			marketBuy: 'Покупка на маркете',
			battleReward: 'Выигрыш в батле',
			cryptoBotRefill: 'Пополнение с Crypto Bot',
			starsRefill: 'Пополнение Stars',
			battleRefund: 'Возврат за Батл',
			adminRefill: 'Пополнение от Админа ❤️',
			rainReward: 'Награда с Дождь',
			rainTip: 'Тип в Дождь',
			tip: 'Тип'
		}
	}
})

const props = defineProps<{
	type:
		| 'deposit_ton'
		| 'deposit_ton_tether'
		| 'deposit_stars'
		| 'deposit_cryptobot'
		| 'withdraw_ton'
		| 'case_open'
		| 'item_sell'
		| 'battle_create'
		| 'battle_join'
		| 'battle_reward'
		| 'referral_claim'
		| 'market_buy'
		| 'item_claim'
		| 'reward_case_open'
		| 'battle_slot_funding_refund'
		| 'referral_case_open'
		| 'welcome_case_open'
		| 'refill_admin'
		| 'rain_reward'
		| 'rain_tip'
	battleInfo?: string
	coins?: number
	date: Date
	case?: {
		uuid: string
		name: string
	}
	item?: {
		modelUuid: string
		modelName: string
		collectionName: string
		price?: number
		chance?: number
	}
	fair?: {
		nonce: number
		clientSeed: string
		serverSeed: string | null
		serverSeedHash: string
		ticketsCount: number
		ticket: number
	}
}>()

const price = computed(() => {
	if (!props.coins) {
		return undefined
	}

	let multiplier = 1

	if (
		[
			'withdraw_ton',
			'case_open',
			'battle_create',
			'battle_join',
			'market_buy',
			'rain_tip'
		].includes(props.type)
	) {
		multiplier = -1
	}

	if (props.type === 'item_claim') {
		return undefined
	}

	return props.coins * multiplier
})

const src = computed(() => {
	if (props.case?.uuid) {
		return caseIcon(props.case.uuid, true)
	}
	if (props.item?.modelUuid) {
		return icon(props.item.modelUuid)
	}
	if (props.type === 'deposit_ton' || props.type === 'withdraw_ton') {
		return tonIcon
	}
	if (props.type === 'deposit_ton_tether') {
		return usdtIcon
	}
	if (props.type === 'deposit_stars') {
		return starsIcon
	}
	if (props.type === 'deposit_cryptobot') {
		return starsIcon
	}
	if (
		props.type === 'battle_create' ||
		props.type === 'battle_join' ||
		props.type === 'battle_reward' ||
		props.type === 'battle_slot_funding_refund'
	) {
		return swordIcon
	}

	return boxIcon
})

const title = computed(() => {
	if (props.type === 'deposit_ton') return t('deposit')
	if (props.type === 'deposit_ton_tether') return t('deposit')
	if (props.type === 'deposit_stars') return t('deposit')
	if (props.type === 'refill_admin') return t('deposit')
	if (props.type === 'rain_reward') return t('deposit')
	if (props.type === 'withdraw_ton') return t('withdraw')
	if (props.type === 'case_open') return t('caseOpened')
	if (props.type === 'item_sell') return t('itemSold')
	if (props.type === 'battle_create') return t('battleCreated')
	if (props.type === 'battle_join') return t('battleJoined')
	if (props.type === 'item_claim') return t('itemClaimed')
	if (props.type === 'reward_case_open') return t('rewardCaseOpened')
	if (props.type === 'referral_case_open') return t('rewardCaseOpened')
	if (props.type === 'welcome_case_open') return t('rewardCaseOpened')
	if (props.type === 'market_buy') return t('marketBuy')
	if (props.type === 'battle_reward') return t('battleReward')
	if (props.type === 'battle_slot_funding_refund') return t('battleRefund')
	if (props.type === 'rain_tip') return t('tip')

	return props.type.split('_').join(' ')
})

const desc = computed(() => {
	if (props.type === 'deposit_ton') return t('tonRefill')
	if (props.type === 'deposit_ton_tether') return t('tonTetherRefill')
	if (props.type === 'deposit_cryptobot') return t('cryptoBotRefill')
	if (props.type === 'deposit_stars') return t('starsRefill')
	if (props.type === 'refill_admin') return t('adminRefill')
	if (props.type === 'rain_reward') return t('rainReward')
	if (
		props.type === 'item_sell' ||
		props.type === 'item_claim' ||
		props.type === 'market_buy'
	)
		if (props.item)
			return `${props.item.modelName} • ${props.item.collectionName}`
		else return t('unknownItem')
	if (
		props.type === 'battle_create' ||
		props.type === 'battle_join' ||
		props.type === 'battle_reward'
	)
		return props.battleInfo
	if (props.type === 'case_open') return props.case?.name || t('unknownCase')
	if (
		props.type === 'reward_case_open' ||
		props.type === 'referral_case_open' ||
		props.type === 'welcome_case_open'
	)
		return props.case?.name || t('unknownCase')
	if (props.type === 'withdraw_ton') return 'TON'
	if (props.type === 'rain_tip') return t('rainTip')
})

const view = ref(false)

const open = () => {
	view.value = true
}

const caseFairness = ref(false)

const openCaseFairness = () => {
	caseFairness.value = true
}

const fairArray = computed(() =>
	props.fair
		? {
				nonce: [props.fair.nonce],
				clientSeed: props.fair.clientSeed,
				serverSeed: props.fair.serverSeed,
				serverSeedHash: props.fair.serverSeedHash,
				ticketsCount: props.fair.ticketsCount,
				ticket: [props.fair.ticket]
			}
		: undefined
)

const box = computed(() =>
	props.case
		? {
				uuid: props.case.uuid,
				name: props.case.name,
				price: props.coins,
				public: props.type === 'case_open' ? true : undefined
			}
		: undefined
)
</script>
